import { Component, inject } from '@angular/core';
import { PageContainerComponent } from '../../layout/page-container/page-container.component';
import { DynamicFormArray, FieldOption, FieldSelectedValue, FormFieldConfig, Props, TabConfig } from '../../model/form.model';
import { LAYOUT_TYPE, OptionsFetchConfig } from '../../model/common.model';
import * as LexusTurnOverModel from '../../model/lexus-turnover.model';
import { FieldNames } from '../../model/form-fields.model';
import { API_STATUS, ApiEnvNames, ERROR_RESPONSE, SearchConfig } from '../../model/api.model';
import { CblHighlightRow, DbFieldKeys, DealerTurnoverReportTabConfig, DistrictTurnoverReportTabConfig, getQueryDatesForTurnOverReport, SearchKeys, Skip, TurnOverReportDataCalculationConfig, TurnoverReportTabConfig, TurnOverTabIds } from '../../model/turn-over.model';
import { BaseComponent } from '../base/base.component';
import { CommonUtils } from '../../utils/common.utils';
import { CommonService } from '../../services/common.service';
import { FormService } from '../../services/form.service';
import { FormGroup, Validators } from '@angular/forms';
import { DataTableSchema } from '../../model/dynamic-data-table.model';
@Component({
  selector: 'app-lexus-turnover-report',
  standalone: true,
  imports: [PageContainerComponent],
  templateUrl: './lexus-turnover-report.component.html',
  styleUrl: './lexus-turnover-report.component.scss'
})
export class LexusTurnoverReportComponent extends BaseComponent {
  protected override dataTableSchema!: DataTableSchema;
  protected data: string = LexusTurnOverModel.LexusTurnOverTitle;
  public filterSchema: DynamicFormArray = structuredClone(LexusTurnOverModel.LexusTurnOverFilterSchema);
  protected turnOverReportDynamicOptionFields: OptionsFetchConfig = structuredClone(LexusTurnOverModel.LexusTurnReportDynamicOptionSchema);
  protected props: Props = LexusTurnOverModel.LexusTurnoverProps;
  tabSchema: Array<TabConfig> = TurnoverReportTabConfig;
  formService = inject(FormService);
  lexusForm: FormGroup | undefined;
  readonly LayoutType: typeof LAYOUT_TYPE = LAYOUT_TYPE;
  title: string = LexusTurnOverModel.LexusTurnOverTitle;
  label = 'Turn Over Report';
  public beginRender = false;
  private readonly commonUtils = inject(CommonUtils);
  private readonly service = inject(CommonService);
  public turnOverResponse:any = {};
  errorShown=false;
  DiagnosticSpecialist = "Diagnostic Specialist/ Service Technician";

  override ngOnInit() {
    super.ngOnInit();
    super.commonFunction();
    this.updateLabel()
    this.setDefaultValue()
    this.disableEnableButtons(true);
    this.formService.form$.subscribe(form => {
      this.lexusForm = form;
    });
  }

  override  onSearchClicked = (_searchParam: SearchConfig) => {
    if(this.lexusForm?.invalid){
      this.lexusForm?.markAllAsTouched()
      return;
    }    
    this.updateTabConfig()
    this.fetchAllData();
  };
  override onResetClicked = () => {
    this.filterSchema.FieldsArray = this.filterSchema.FieldsArray.map(
      (field: FormFieldConfig) => {
        if (field.fieldName === FieldNames.District) {
          field.disabled = true;
        }
        return { ...field, selectedValue: field['defaultValue'] ? field['defaultValue'] : '' };
      }
    );
    this.resetDataTable();
    this.setDefaultValue()
    this.updateLabel()
    this.checkForAnyEmptyFields();
  };
  resetDataTable(){
    this.tabSchema.forEach(tab=>{
      if(tab['inputs']['DataTableSchema']){
        tab['inputs']['DataTableSchema']['dataSource'] = [];
        tab['inputs']['DataTableSchema']['activeSorts'] = [];
        tab['inputs']['DataTableSchema']['inputFilter'] = '';
        tab['inputs']['dataInput'] = [];
      }
    })
  }
  override updateOptions = () => {
    this.service.getDataFromDataSource(this.turnOverReportDynamicOptionFields.searchConfig, ApiEnvNames['TurnOverReport']).then(response => {
      if (response['status'] === API_STATUS['Success']) {
        if (Array.isArray(response.result)) {
          this.updateJobRoleOptions(response.result)
          this.checkForAnyEmptyFields();
        }
      } else {
         this.commonUtils.showMatSnackBar(ERROR_RESPONSE['errorFetch'])
      }      
    }).catch(err => {
      console.info(ERROR_RESPONSE['layoutErrorPrefix'], err);
    })
  }
  override  onControlValueChanged = (event: FieldSelectedValue) => {
    this.filterSchema.FieldsArray = this.filterSchema.FieldsArray.map((filterField) => {
      if(filterField['fieldName'] === event['FieldName']){
        filterField.selectedValue = event.Value;
      }
      if(filterField['isDependentOn'] === event['FieldName']){
        const control = this.lexusForm?.get(FieldNames['District']);
        if([undefined, null, '', []].includes(event['Value'])){
          filterField['disabled'] = true;
          filterField['selectedValue'] = '';
          control?.disable()
        }else{
          filterField['disabled'] = false;
          control?.enable()
        }
        control?.updateValueAndValidity();
      }
      return filterField;
    });
    const nonCertifiableRoles = this.filterSchema.FieldsArray.find(x=>x['fieldName'] === FieldNames['NonCertifiableRoles']) ?? {} as FormFieldConfig;
    const certifiableRoles = this.filterSchema.FieldsArray.find(x=>x['fieldName'] === FieldNames['CertifiableRoles']) ?? {} as FormFieldConfig;  
    const certifiableRolesControl = this.lexusForm?.get(FieldNames['CertifiableRoles']);
    const nonCertifiableRolesControl = this.lexusForm?.get(FieldNames['NonCertifiableRoles']);
    if(event['FieldName'] === FieldNames['CertifiableRoles'] && ![undefined, null, '', []].includes(event['Value']) ){
      nonCertifiableRoles.selectedValue = '';
      nonCertifiableRoles.required = false;
      certifiableRoles.required = true;
      certifiableRolesControl?.setValidators([Validators.required])
      nonCertifiableRolesControl?.setValidators([])
    }
    if(event['FieldName'] === FieldNames['NonCertifiableRoles'] && ![undefined, null, '', []].includes(event['Value']) ){
     certifiableRoles.selectedValue = '';
     certifiableRoles.required = false;
     nonCertifiableRoles.required = true;
     nonCertifiableRolesControl?.setValidators([Validators.required])
     certifiableRolesControl?.setValidators([])
    }
    this.updateLabel()
    this.checkForAnyEmptyFields();
  }
  override checkForAnyEmptyFields = () => {
    let enableButtons = true;
    const emptyFieldsLength = this.filterSchema.FieldsArray.some((p) => {
    const val = p.selectedValue;  
      return !!val && ((typeof val === 'string' && val.trim() !== '') || 
        (Array.isArray(val) && val.length > 0) ||
        (typeof val === 'object' && !Array.isArray(val) && Object.keys(val).length > 0))
    });
    enableButtons = !emptyFieldsLength;
    if(!emptyFieldsLength){
        this.resetDataTable();
    }
    this.disableEnableButtons(enableButtons);
  };
  updateJobRoleOptions(result:any){
    const jobRoles = result[0];
    const jobRolesOptions :Record<string,FieldOption[]> = {};
    Object.keys(jobRoles).forEach((key: string)=>{
      jobRolesOptions[key] = jobRoles[key].map((jobRoleStr: string | number)=> ({'fieldLabel': jobRoleStr, 'fieldValue': jobRoleStr }))
    })
    this.filterSchema.FieldsArray.forEach(field => {
      if (field['fieldName'] === FieldNames['CertifiableRoles']) {
        jobRolesOptions['certifiableRoles'].push({fieldValue:this.DiagnosticSpecialist, fieldLabel:this.DiagnosticSpecialist})
        field['options'] = jobRolesOptions['certifiableRoles']
        field['isLoading'] = false
        field['selectedValue'] = this.DiagnosticSpecialist
      }else if(field['fieldName'] === FieldNames['NonCertifiableRoles']){
        field['options'] = jobRolesOptions['nonCertifiableRoles']
        field['isLoading'] = false
      }
    })
  }
  getPayload = ()=>{
    const postParam = structuredClone(LexusTurnOverModel.LexusTurnOverReportSearchConfig)
    const getBody = (body:any)=>{
      const getReportType = (payload:any)=>{
        let reportType = null;
        if(payload[FieldNames['Area']] === ''){
          reportType = 'area'
        }else if(payload[FieldNames['District']] === ''){
          reportType = 'district'
        }else{
          reportType = 'dealer'
        }
        return reportType;
      }
      this.filterSchema['FieldsArray'].forEach(field => {
        if (field['selectedValue'] && field['selectedValue'] !== null && field['selectedValue'] !== '') {
          if(field['fieldName'] === FieldNames['CertifiableRoles'] || field['fieldName'] === FieldNames['NonCertifiableRoles']){
            body['jobRole'] = field['selectedValue']
          }else{
            body[field['fieldName']] = field['selectedValue']
          }
        }
      });
      body['reportType'] = getReportType(body);
      body['startDate'] = this.getStartAndEndDate()['startDate'];
      body['endDate'] = this.getStartAndEndDate()['endDate'];
      return body
    }
    Object.values(postParam).forEach(searchConfig=>{
      searchConfig['body'] = getBody(searchConfig['body'])
    })
    return postParam;
  }
  setDefaultValue(){
    const date = getQueryDatesForTurnOverReport('end');
    const month = new Date(date).getMonth() + 1;
    const year = new Date(date).getFullYear();
    this.filterSchema.FieldsArray.forEach(field=>{
      if(field['fieldName'] === FieldNames['Month']){
        field['selectedValue'] = month.toString()
        field['defaultValue'] = month.toString()
      }
      if(field['fieldName'] === FieldNames['Year']){
        field['selectedValue'] = year.toString()
        field['defaultValue'] = year.toString()
      }
    })
  }
  getStartAndEndDate(){
    let endDate: any = getQueryDatesForTurnOverReport('end');
    let startDate: any = getQueryDatesForTurnOverReport('start'); 
    const selectedMonth = this.filterSchema.FieldsArray.find(field => field['fieldName'] === FieldNames['Month']);
    const selectedYear = this.filterSchema.FieldsArray.find(field => field['fieldName'] === FieldNames['Year']);
    const selectedRange = this.filterSchema.FieldsArray.find(field => field['fieldName'] === FieldNames['Range']);
    const formatTwoDigits = (num: number) => String(num).padStart(2, '0');
    let month = new Date(endDate).getMonth();
    let year = new Date(endDate).getFullYear();
    if(selectedMonth && selectedMonth['selectedValue'] !== ''){
      month = Number(selectedMonth['selectedValue']) - 1;
    }
    if (selectedYear && selectedYear['selectedValue'] !== '') {
      year = Number(selectedYear['selectedValue']);
    }
    const end = new Date(year, month + 1, 0);
    if(selectedRange?.['selectedValue'] === 'year'){
      endDate = `${end.getFullYear()}-${formatTwoDigits(end.getMonth() + 1)}-${formatTwoDigits(end.getDate())}`;
      const start = new Date(year, month - 11, 1);
      startDate = `${start.getFullYear()}-${formatTwoDigits(start.getMonth() + 1)}-01`;
    }else{
      endDate = `${end.getFullYear()}-${formatTwoDigits(end.getMonth() + 1)}-${formatTwoDigits(end.getDate())}`;
      startDate = `${end.getFullYear()}-${formatTwoDigits(end.getMonth() + 1)}-01`;
    }
    return {
      startDate,
      endDate
    };
  }
  updateLabel(){     
    const startDate = this.getStartAndEndDate()['startDate'];
    const endDate = this.getStartAndEndDate()['endDate'];
    
    const formatDate = (dateStr:string) => {
      const mmddyyyyPattern = /^\d{2}\/\d{2}\/\d{4}$/;
      if (mmddyyyyPattern.test(dateStr)) {
        return dateStr;
      }
      const [year, month, day] = dateStr.split("-");
      return `${month}/${day}/${year}`;
    }
  
    const turnOverReportLabel = `Turnover report from ${formatDate(startDate)} to ${formatDate(endDate)}`;
    if(this.label){
      this.label = turnOverReportLabel;
    }
  }
  fetchTurnOverData(searchConfig:SearchConfig, key:string){
    const errorHandler = (msg:any)=>{
      this.turnOverResponse[key] = []
      this.commonUtils.showMatSnackBar(ERROR_RESPONSE['errorFetch'])
      return Promise.reject(new Error(msg));
    }
    return this.service.getDataFromDataSource(searchConfig, ApiEnvNames['TurnOverReport']).then(res => {
      if(res['status'] === API_STATUS['Success']) {
        this.turnOverResponse[key] = res['result']
        return this.turnOverResponse;
      }else {
        return errorHandler(res['errorMsg'])
      }
    }).catch(err=>{
      return errorHandler(err)
    })
  }
  async fetchAllData(){
    this.resetDataTable();
    this.tabSchema.forEach(tab => {
      if (tab['inputs']['DataTableSchema']) {
        tab['inputs']['DataTableSchema'].isLoading = true;
      }
    });
    const searchConfigs = this.getPayload();
    const taskList: any[] = [];
    this.turnOverResponse = {};
    Object.keys(searchConfigs).forEach(searchConfigKey=>{
      taskList.push(this.fetchTurnOverData(searchConfigs[searchConfigKey], searchConfigKey));
    })
    return Promise.all(taskList).then(()=>{
      this.handleTurnOverReportResult(this.turnOverResponse);
      this.tabSchema.forEach(tab => {
        if (tab['inputs']['DataTableSchema']) {
          tab['inputs']['DataTableSchema'].isLoading = false;
        }
      });
    }).catch(err=>{
      console.info(ERROR_RESPONSE['layoutErrorPrefix'],err);
      this.tabSchema.forEach(tab => {
        if (tab['inputs']['DataTableSchema']) {
          tab['inputs']['DataTableSchema'].isLoading = false;
        }
      });
    })
  }
  handleTurnOverReportResult(dataSet:any){
    this.tabSchema.forEach(tab=>{
      if(tab.inputs?.DataTableSchema){
        tab['inputs']['dataInput'] = [];
      }
      let dataSource = []
      if(tab['tabId'] === TurnOverTabIds['area'] || tab['tabId'] === TurnOverTabIds['areaCertification'] || tab['tabId'] === TurnOverTabIds['nationalCertification']){
        dataSource = this.handleResponseForArea(tab['tabId'], dataSet);
      }else if(tab['tabId'] === TurnOverTabIds['district'] || tab['tabId'] === TurnOverTabIds['districtCertification']){
        dataSource = this.handleResponseForDistrict(tab['tabId'], dataSet);
      }else if(tab['tabId'] === TurnOverTabIds['dealer'] || tab['tabId'] === TurnOverTabIds['dealerCertification']){
        dataSource = this.handleResponseForDealer(tab['tabId'], dataSet);
      }
      if(tab.inputs?.DataTableSchema){
        tab.inputs.DataTableSchema.dataSource = dataSource
      }
      tab['inputs']['dataInput'] = dataSource
    })
  }
  handleResponseForArea(id:string, dataSet:any){
    if(id === TurnOverTabIds['area']){
      const data:any = this.formatDataForReport(dataSet[SearchKeys['Area']], {key:'areaName', label: 'National'})
      return [...data['records'], ...data['consolidateData']]
    }else if(id === TurnOverTabIds['areaCertification']){
      const data = structuredClone(dataSet[SearchKeys['Certificate']]);
      const result:any = this.groupAndFormatDataForReport(data,{key:'areaName', label:'National'});
      return [...result['records'], ...result['consolidateData']]
    }else{
      return this.handleNationalCert(dataSet);
    }
  }
  handleResponseForDistrict(id:string, dataSet:any){
    if(TurnOverTabIds['district'] === id){
      const data = structuredClone(dataSet[SearchKeys['Area']]);
      const result:any = this.formatDataForReport(data,{key:'district', label:'National', consolidateFieldKey:'areaName'});
      return [...result['records'], ...result['consolidateData']]
    }else{
      const data = structuredClone(dataSet[SearchKeys['Certificate']]);
      const result:any = this.groupAndFormatDataForReport(data,{key:'district', label:'National'}, false);
      return [...result['records'], ...result['consolidateData']]
    }
  }
  handleResponseForDealer(id:string, dataSet:any){
    const updateRecord = (record:any)=>{
      record['dealerName'] = `${record['dealer']} - ${record['dealerName']}`
    }
    if(TurnOverTabIds['dealer'] === id){
      const data = structuredClone(dataSet[SearchKeys['Area']]);
      const result:any = this.formatDataForReport(data,{key:'dealerName', label:'National', consolidateFieldKey:'areaName'}, false, updateRecord);
      return [...result['records'], ...result['consolidateData']]
    }else{
      const data = structuredClone(dataSet[SearchKeys['Certificate']]);
      const result:any = this.groupAndFormatDataForReport(data,{key:'dealerName', label:'National'}, false, updateRecord);
      return [...result['records'], ...result['consolidateData']]
    }
  }
  handleNationalCert(dataSet:any){
    const certData: any[] = structuredClone(dataSet[SearchKeys['Certificate']]);
    const result:any = {};
    const summary:any = { certLevel: 'Total', areaName: 'National' };
    certData.forEach(entry => {
      const certLevel = entry.certLevel;
      result[certLevel] ??= { certLevel };
      for (const key in entry) {
        if (key !== 'certLevel' && key !== 'areaName') {
          const value = Number.parseFloat(entry[key]);
          if (!Number.isNaN(value)) {
            result[certLevel][key] = (result[certLevel][key] || 0) + value;
            summary[key] = (summary[key] ?? 0) + value;
          }
        }
      }
      result[certLevel]['areaName'] = certLevel;
    });
    Object.values(result).forEach((data:any) => {
      Object.keys(data).forEach(fieldKey => {
        if (fieldKey.includes('Percent')) {
          const key = fieldKey.split('Percent')[0]
          if (data['startPeriod'] === 0) {
            const value = 0;
            data[fieldKey] = value.toFixed(2)
          } else {
            const percent = (data[key] / data['startPeriod']) * 100
            data[fieldKey] = percent.toFixed(2)
          }
        }
      })
    })
    Object.keys(summary).forEach(fieldKey => {
      if (fieldKey.includes('Percent')) {
        const key = fieldKey.split('Percent')[0]
        if(summary['startPeriod'] === 0){
          const value = 0;
          summary[fieldKey] = value.toFixed(2)
        }else{
          const percent = (summary[key] / summary['startPeriod']) * 100
          summary[fieldKey] = percent.toFixed(2)
        }

      }
    })
    summary[CblHighlightRow] = true;
    return [summary, ...Object.values(result)];
  }
  updateTabConfig(){
    let isAreaSelected = false;
    let isDistrictSelected = false;
    this.filterSchema.FieldsArray.forEach(field=>{
      if(field['fieldName'] === FieldNames['Area'] && ![undefined, null, '', []].includes(field['selectedValue'])){
        isAreaSelected = true
      }else if(field['fieldName'] === FieldNames['District'] && ![undefined, null, '', []].includes(field['selectedValue'])){
        isDistrictSelected = true;
      }
    })
    if(isAreaSelected && isDistrictSelected){
      this.tabSchema = DealerTurnoverReportTabConfig
    }else if(isAreaSelected && !isDistrictSelected){
      this.tabSchema = DistrictTurnoverReportTabConfig
    }else{
      this.tabSchema = TurnoverReportTabConfig
    }
  }
  groupAndFormatDataForReport(records:any[], displayConfig:{key:string, label:string},consolidateRequired = true, updateLabelCallBack?:any){
    if(records.length<=0){
      return {
        records: [],
        consolidateData: []
      }
    }
    const result:any = {}
    records.forEach((record:any)=>{
      if(updateLabelCallBack && typeof updateLabelCallBack === 'function'){
        updateLabelCallBack(record)
      }
      const groupKey = record[displayConfig['key']]
      result[groupKey] ??= [];
      result[groupKey].push(record)
    })
    const dataArr:any[] = [];
    let consolidateData:any = {};    
    const calcConfig = TurnOverReportDataCalculationConfig;  
    const fieldNames = Object.keys(calcConfig);
    const sumAllFields = (data:any, result:any)=>{
      for (const key in data) {
        if(fieldNames.includes(key)){
          const value = Number.parseFloat(data[key]);
          if (Number.isNaN(value)) {
            result[key] = data[key] ;
          }else{
            result[key] = (result[key] ?? 0) + value;
          }
        }else{
          result[key] = ''
        }
      }
      return result
    }
    Object.keys(result).forEach(groupKey=>{
      const data:any = this.formatDataForReport(result[groupKey], {key:displayConfig['key'], label: groupKey}, true, updateLabelCallBack)
      dataArr.push(...data['consolidateData'], ...data['records'])
      consolidateData = sumAllFields(data['consolidateData'][0], consolidateData)
    })
    const percentageFields = fieldNames.filter(field=> calcConfig[field] !== Skip);
    if(percentageFields.length>0){
      percentageFields.forEach(fieldName=>{
        let value:any = 0;
        if(consolidateData[DbFieldKeys['StartPeriod']] !== 0){
          value = ((consolidateData[fieldName] / consolidateData[DbFieldKeys['StartPeriod']]) * 100 ).toFixed(2)
        }
        consolidateData[calcConfig[fieldName]] = value;
      })
  }
    consolidateData[displayConfig['key']] = displayConfig['label'];
    consolidateData[CblHighlightRow] = true;
    let groupedData: any[] = []
    if(consolidateRequired){
      groupedData = [consolidateData]
    }
    return {
      records: dataArr,
      consolidateData: groupedData
    }
  }
  formatDataForReport(records:any[], displayConfig:{key:string, label:string, consolidateFieldKey?:string}, calculateCertLevel=false, updateLabelCallBack?:any){
    if(records.length<=0){
      return {
        records: [],
        consolidateData: []
    }
    }
    let consolidateData:any = {};
    const calcConfig = TurnOverReportDataCalculationConfig;
    const fieldNames = Object.keys(calcConfig);
    const sumAllFields = (data:any, result:any)=>{
      for (const key in data) {
        if(fieldNames.includes(key)){
          const value = Number.parseFloat(data[key]);
          if (Number.isNaN(value)) {
            result[key] = data[key];
          }else{
            result[key] = (result[key] ?? 0) + value;
          }
        }else{
          result[key] = data[key]
        }
      }
      return result
    }
    records.forEach(record=>{
      if(updateLabelCallBack && typeof updateLabelCallBack === 'function'){
        updateLabelCallBack(record)
      }
      consolidateData = sumAllFields(record, consolidateData)
      if(calculateCertLevel){
        record[displayConfig['key']] = record['certLevel']
      }
    })
    const percentageFields = fieldNames.filter(field=> calcConfig[field] !== Skip);
      if(percentageFields.length>0){
        percentageFields.forEach(fieldName=>{
          let value:any = 0;
          if(consolidateData[DbFieldKeys['StartPeriod']] !== 0){
            value = ((consolidateData[fieldName] / consolidateData[DbFieldKeys['StartPeriod']]) * 100).toFixed(2)
          }
          consolidateData[calcConfig[fieldName]] = value;
        })
    }
    if(displayConfig['consolidateFieldKey']){
      consolidateData[displayConfig['key']] = consolidateData[displayConfig['consolidateFieldKey']];
    }else{
      consolidateData[displayConfig['key']] = displayConfig['label'];
    }
    consolidateData[CblHighlightRow] = true;
    return {
      records: records,
      consolidateData: [consolidateData]
    }
  }
}